import { debounce } from "lodash/fp";

import getFetchStrategy, { FetchStrategy } from "@app/common/api/fetchStrategy";
import ResponseError from "@app/common/api/responseError";
import { ApiOptions } from "@app/common/api/types";
import Config from "@app/config";
import logger from "@app/utils/logger";
import { camelizeKeys, snakeKeys } from "@app/utils/utils";

const PROTOCOL_REGEX = /^https?:\/\//;

const getUrlFromPath = (path: string) =>
  path.match(PROTOCOL_REGEX) ? path : `${Config.api.HOST}${path}`;

const dispatchRefreshTokenFailed = debounce(500, () => {
  window.dispatchEvent(new Event("refresh_token_failed"));
});

let tokenRefreshPromise: Promise<boolean> | null = null;
let wasLastRefreshAttemptSuccessful = true;
let lastRefreshAttemptFailureMillis = 0;

const attemptTokenRefresh = async (): Promise<boolean> => {
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  } else if (new Date().valueOf() - lastRefreshAttemptFailureMillis < 2000) {
    // in the case a failure has happened in the last couple seconds, let's not re-attempt
    // this can happen while we are waiting for the page to be navigated back into sign-in
    // if the token request was not queued alongside the first refresh

    return wasLastRefreshAttemptSuccessful;
  }

  tokenRefreshPromise = (async () => {
    try {
      await rawPost("/users/me/token", {}, { withCreds: true });
      wasLastRefreshAttemptSuccessful = true;
    } catch {
      logger.info("failed to refresh access token, please re-authenticate");
      if (window) {
        dispatchRefreshTokenFailed();
      }
      wasLastRefreshAttemptSuccessful = false;
      lastRefreshAttemptFailureMillis = new Date().valueOf();
    } finally {
      tokenRefreshPromise = null;
    }

    return wasLastRefreshAttemptSuccessful;
  })();

  return tokenRefreshPromise;
};

const tokenRetry =
  (httpCall: (...args: any) => Promise<any>) =>
  async (...args: any) => {
    try {
      return await httpCall(...args);
    } catch (error: any) {
      if (error.response?.status === 401) {
        // refresh token, if success, then retry, otherwise fail
        // with original error
        logger.info("attempting to refresh access token...");
        const refreshSuccess = await attemptTokenRefresh();
        if (refreshSuccess) {
          return await httpCall(...args);
        }
      }
      throw error;
    }
  };

// TODO: pass through httpcall types
const withFetch = <F extends (...args: any[]) => any>(
  fetchFn: typeof fetch,
  httpCall: F
) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return async (...args: any[]) => {
    return httpCall(...args, fetchFn);
  };
};

// tokenRetry uses this method to make the refresh attempt,
// we don't want the call that makes the refresh attempt, to make
// a refresh attempt if it fails :)
export const rawPost = async (
  path: string,
  data: any,
  options: ApiOptions = {
    withCreds: false,
    authBearer: undefined,
    parseErrorResponse: false,
  },
  fetchFn: typeof fetch = fetch
) => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(options.authBearer && {
        Authorization: `Bearer ${options.authBearer}`,
      }),
    },
    body: JSON.stringify(snakeKeys(data)),
    credentials: options.withCreds ? "include" : "omit",
  });

  if (response.status === 204) return; // handle success with no body

  const shouldParseError =
    options.parseErrorResponse &&
    (!options.parseErrorResponseStatuses ||
      options.parseErrorResponseStatuses.includes(response.status));

  if (!response.ok && !shouldParseError) {
    throw new ResponseError(response);
  }

  try {
    const json = await response.json();

    return camelizeKeys(json);
  } catch (error) {
    logger.warn("json could not be parsed from response body");
    return;
  }
};

export const rawGet = async <T>(
  path: string,
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  fetchFn: typeof fetch = fetch
): Promise<T | null> => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "GET",
    headers: {
      ...(options.authBearer && {
        Authorization: `Bearer ${options.authBearer}`,
      }),
    },
    credentials: options.withCreds ? "include" : "omit",
  });

  if (!response.ok) {
    throw new ResponseError(response);
  }

  if (response.status === 204) return null; // handle success with no body

  try {
    const json = await response.json();

    return camelizeKeys(json) as T;
  } catch (error: any) {
    logger.info("json could not be parsed from response body");
    return null;
  }
};

export const rawPut = async (
  path: string,
  data: any,
  options: ApiOptions = {
    withCreds: false,
    authBearer: undefined,
    parseErrorResponse: false,
  },
  fetchFn: typeof fetch = fetch
) => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(options.authBearer && {
        Authorization: `Bearer ${options.authBearer}`,
      }),
    },
    body: JSON.stringify(snakeKeys(data)),
    credentials: options.withCreds ? "include" : "omit",
  });

  if (response.status === 204) return; // handle success with no body

  if (!response.ok && !options.parseErrorResponse) {
    throw new ResponseError(response);
  }

  try {
    const json = await response.json();
    return camelizeKeys(json);
  } catch (error) {
    logger.warn("json could not be parsed from response body");
    return;
  }
};

export const rawPatch = async (
  path: string,
  data: any,
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  fetchFn: typeof fetch = fetch
) => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(options.authBearer && {
        Authorization: `Bearer ${options.authBearer}`,
      }),
    },
    body: JSON.stringify(snakeKeys(data)),
    credentials: options.withCreds ? "include" : "omit",
  });

  if (response.status === 204 || response.status === 202) return true; // handle success with no body

  if (!response.ok) {
    throw new ResponseError(response);
  }

  const json = await response.json();
  return camelizeKeys(json);
};

export const rawDelete = async (
  path: string,
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  data?: any,
  fetchFn: typeof fetch = fetch
): Promise<boolean> => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...(options.authBearer && {
        Authorization: `Bearer ${options.authBearer}`,
      }),
    },
    ...(data && {
      body: JSON.stringify(snakeKeys(data)),
    }),
    credentials: options.withCreds ? "include" : "omit",
  });

  if (response.status === 204) return true; // handle success with no body

  if (!response.ok) {
    throw new ResponseError(response);
  }

  return true;
};

export const rawUploadFile = async (
  path: string,
  file: File,
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  fetchFn: typeof fetch = fetch
) => {
  const data = new FormData();
  data.append("file", file);

  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "PUT",
    headers: {
      ...(options.authBearer && {
        Authorization: `Bearer ${options.authBearer}`,
      }),
    },
    body: data,
    credentials: options.withCreds ? "include" : "omit",
  });

  if (!response.ok) {
    throw new ResponseError(response);
  }

  return Promise.resolve();
};

export const rawGetBlobUrl = async (
  path: string,
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  fetchFn: typeof fetch = fetch
) => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "GET",
    headers: {
      ...(options.authBearer && {
        Authorization: `Bearer ${options.authBearer}`,
      }),
    },
    credentials: options.withCreds ? "include" : "omit",
  });

  if (!response.ok) {
    throw new ResponseError(response);
  }

  try {
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error: unknown) {
    logger.info("blob could not be generated from response");
    return null;
  }
};

const getApi = (fetchStrategy: FetchStrategy) => {
  const doFetch = getFetchStrategy(fetchStrategy);
  const rawPostWithFetch = withFetch(doFetch, rawPost);
  const rawGetWithFetch = withFetch(doFetch, rawGet);
  const rawPutWithFetch = withFetch(doFetch, rawPut);
  const rawPatchWithFetch = withFetch(doFetch, rawPatch);
  const rawDeleteWithFetch = withFetch(doFetch, rawDelete);
  const rawUploadFileWithFetch = withFetch(doFetch, rawUploadFile);
  const rawGetBlobUrlWithFetch = withFetch(doFetch, rawGetBlobUrl);

  const post = tokenRetry(rawPostWithFetch);
  const get = tokenRetry(rawGetWithFetch);
  const put = tokenRetry(rawPutWithFetch);
  const patch = tokenRetry(rawPatchWithFetch);
  const httpDelete = tokenRetry(rawDeleteWithFetch);
  const uploadFile = tokenRetry(rawUploadFileWithFetch);
  const getBlobUrl = tokenRetry(rawGetBlobUrlWithFetch);

  return {
    rawPost: rawPostWithFetch,
    rawGet: rawGetWithFetch,
    rawGetBlobUrl: rawGetBlobUrlWithFetch,
    rawPut: rawPutWithFetch,
    rawPatch: rawPatchWithFetch,
    rawDelete: rawDeleteWithFetch,
    rawUploadFile: rawUploadFileWithFetch,
    post,
    get,
    getBlobUrl,
    put,
    patch,
    httpDelete,
    uploadFile,
  };
};

export default getApi;
